import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
    getRejectedImportFiles,
    publisherResultImportsApi,
    type PublisherResultImportApprovalResult,
} from '@/api/publisher-result-imports';
import { uploadsApi } from '@/api/uploads';
import { Danger, Tick, Spinner, Trash, ExportCurve, Cross } from '@/components/icons';
import { WizardReviewStepFooter } from '@/components/modals/WizardReviewStepFooter';
import {
    ReportImportHistoryList,
    useReportImportHistory,
} from '@/components/publisher-results/ReportImportHistory';
import { AppDialogLayout, SegmentedTabs } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { Progress } from '@/components/ui/progress';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import {
    getDefaultSelectedPublisherResultTableIds,
    isPublisherResultImportActive,
} from '@/lib/publisher-result-imports';
import { importNeedsAttention, STATUS_META, withEllipsis } from './import-status';
import { PublisherResultGuidedReview } from './PublisherResultGuidedReview';
import { PublisherResultProcessingPanel } from './PublisherResultProcessingPanel';
import type {
    PublisherResultImport,
    PublisherResultImportBatch,
    RejectedImportFile,
} from '@/types/publisher-result-import';
import type { ReportData } from '@/types/report';

const MAX_FILES = 10;
const MAX_FILE_BYTES = 15 * 1024 * 1024;

type PublisherResultImportModalProps = {
    patientId: string;
    mode: 'patient' | 'report';
    sourceReportId?: string | null;
    initialBatchId?: string | null;
    initialImportId?: string | null;
    reportData?: ReportData;
    onBeforeApprove?: () => Promise<void> | void;
    onApproved?: (result: PublisherResultImportApprovalResult) => Promise<void> | void;
    onCompleted?: () => void;
    onBackToMenu?: () => void;
    reviewNavigation?: {
        currentIndex: number;
        total: number;
        onPrevious: () => void;
        onNext: () => void;
        onSelect: (index: number) => void;
    };
    onReviewApproved?: (importId: string) => void;
    onClose: () => void;
};

type UploadProgress = Record<string, number>;
type UploadTab = 'upload' | 'history';
type SelectionState = Record<string, string[]>;

const isPdfFile = (file: File) =>
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

const getErrorMessage = (error: unknown) => {
    if (error && typeof error === 'object' && 'response' in error) {
        const response = (
            error as {
                response?: {
                    data?: { description?: string; message?: string };
                };
            }
        ).response;
        return response?.data?.description || response?.data?.message;
    }

    if (error instanceof Error) return error.message;
    return null;
};

function ImportCard({
    record,
    onContinueDuplicate,
    onCancel,
    continuing,
    cancelling,
}: {
    record: PublisherResultImport;
    onContinueDuplicate: () => void;
    onCancel: () => void;
    continuing: boolean;
    cancelling: boolean;
}) {
    const meta = STATUS_META[record.status];

    return (
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <h4 className="truncate text-sm font-semibold text-foreground">
                        {record.original_filename}
                    </h4>
                </div>
            </div>

            {record.safe_error_message && (
                <Alert variant="destructive" icon={<Danger className="size-4" />} className="mt-3">
                    {record.safe_error_message}
                </Alert>
            )}

            {isPublisherResultImportActive(record) && (
                <div className="mt-4 rounded-2xl border border-info/20 bg-info-subtle p-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                            <Spinner className="mt-0.5 size-4 shrink-0 animate-spin text-info" />
                            <div>
                                <p className="text-sm font-medium text-foreground">
                                    {withEllipsis(meta.description)}
                                </p>
                            </div>
                        </div>
                        <TooltipIconButton
                            label="Cancelar importação"
                            onClick={onCancel}
                            disabled={cancelling}
                            className="self-end text-muted-foreground hover:bg-accent hover:text-foreground sm:self-auto"
                        >
                            {cancelling ? (
                                <Spinner className="size-4 animate-spin" />
                            ) : (
                                <Cross className="size-4" />
                            )}
                        </TooltipIconButton>
                    </div>
                </div>
            )}

            {record.status === 'duplicate_warning' && (
                <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-amber-900">
                        Este arquivo parece já ter sido importado para o paciente.
                    </div>
                    <div className="flex items-center gap-2 sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onContinueDuplicate}
                            disabled={continuing || cancelling}
                        >
                            {continuing && <Spinner className="size-4 animate-spin" />}
                            Processar mesmo assim
                        </Button>
                        <TooltipIconButton
                            label="Cancelar importação"
                            onClick={onCancel}
                            disabled={continuing || cancelling}
                        >
                            {cancelling ? (
                                <Spinner className="size-4 animate-spin" />
                            ) : (
                                <Cross className="size-4" />
                            )}
                        </TooltipIconButton>
                    </div>
                </div>
            )}

            {record.status === 'approved' && (
                <div className="mt-4 flex items-center gap-2 rounded-2xl border border-green-100 bg-green-50 p-3 text-sm text-green-700">
                    <Tick className="size-4" />
                    Importação aprovada.
                </div>
            )}

            {record.status === 'approved_pending_report' && (
                <div className="mt-4 flex items-center gap-2 rounded-2xl border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">
                    <Tick className="size-4" />
                    Importação aprovada. Ela será adicionada quando houver um laudo em andamento.
                </div>
            )}
        </div>
    );
}

export function PublisherResultImportModal({
    patientId,
    mode,
    sourceReportId,
    initialBatchId = null,
    initialImportId = null,
    reportData,
    onBeforeApprove,
    onApproved,
    onCompleted,
    onBackToMenu,
    reviewNavigation,
    onReviewApproved,
    onClose,
}: PublisherResultImportModalProps) {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [batchId, setBatchId] = useState<string | null>(initialBatchId);
    const [selectedImportId, setSelectedImportId] = useState<string | null>(initialImportId);
    const [createdBatch, setCreatedBatch] = useState<PublisherResultImportBatch | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
    const [error, setError] = useState<string | null>(null);
    const [rejectedFiles, setRejectedFiles] = useState<RejectedImportFile[]>([]);
    const [selection, setSelection] = useState<SelectionState>({});
    const [approvingId, setApprovingId] = useState<string | null>(null);
    const [continuingId, setContinuingId] = useState<string | null>(null);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    // Only the upload step has tabs, and only from a report: the patient page
    // already lists the imports underneath the modal.
    const [uploadTab, setUploadTab] = useState<UploadTab>('upload');

    const batchQuery = useQuery({
        queryKey: ['publisher-result-import-batch', batchId],
        queryFn: () => publisherResultImportsApi.getBatch(batchId || ''),
        enabled: Boolean(batchId),
        refetchInterval: (query) =>
            query.state.data?.imports.some(isPublisherResultImportActive) ? 2_500 : false,
    });

    const batch = batchQuery.data ?? createdBatch;
    const hasBatch = Boolean(batchId);
    const batchImports = useMemo(() => batch?.imports ?? [], [batch]);
    const visibleImports = useMemo(() => {
        if (!initialImportId) return batchImports;
        return batchImports.filter((record) => record.id === initialImportId);
    }, [batchImports, initialImportId]);
    const activeCount = visibleImports.filter(isPublisherResultImportActive).length;
    const selectedImportIndex = visibleImports.findIndex(
        (record) => record.id === selectedImportId,
    );
    const selectedImport =
        selectedImportIndex >= 0 ? visibleImports[selectedImportIndex] : visibleImports[0];
    const selectedImportPosition = selectedImportIndex >= 0 ? selectedImportIndex : 0;
    const activeSelectedTableIds = selectedImport ? (selection[selectedImport.id] ?? []) : [];
    const isApprovingSelectedImport = selectedImport ? approvingId === selectedImport.id : false;
    const showsHistory = mode === 'report' && Boolean(sourceReportId);
    const history = useReportImportHistory({
        patientId,
        reportId: sourceReportId,
        enabled: showsHistory && !batchId,
    });
    // An import ready for approval never reaches this layout — the guided
    // review takes the whole dialog before it renders.
    const isProcessingStep = activeCount > 0;
    // Imports waiting on a decision are pulled out of the quiet processing
    // list, so the one thing that needs the psychologist is not buried
    // between spinners.
    const attentionImports = visibleImports.filter(importNeedsAttention);
    const processingImports = visibleImports.filter(isPublisherResultImportActive);
    const modalTitle = isProcessingStep
        ? 'Processando resultados importados'
        : 'Importar resultados de editoras';

    useEffect(() => {
        if (!batch) return;

        setSelection((current) => {
            let changed = false;
            const next = { ...current };

            for (const record of batch.imports) {
                if (record.status !== 'ready_for_approval' || next[record.id]) continue;
                next[record.id] = getDefaultSelectedPublisherResultTableIds(record);
                changed = true;
            }

            return changed ? next : current;
        });
    }, [batch]);

    useEffect(() => {
        if (visibleImports.length === 0) return;

        setSelectedImportId((current) => {
            if (initialImportId && visibleImports.some((record) => record.id === initialImportId)) {
                return initialImportId;
            }

            const currentRecord = visibleImports.find((record) => record.id === current);
            const firstReadyImport = visibleImports.find(
                (record) => record.status === 'ready_for_approval',
            );

            if (firstReadyImport && currentRecord?.status !== 'ready_for_approval') {
                return firstReadyImport.id;
            }

            if (currentRecord) {
                return current;
            }

            return firstReadyImport?.id ?? visibleImports[0].id;
        });
    }, [initialImportId, visibleImports]);

    const uploadPercent = useMemo(() => {
        if (files.length === 0) return 0;
        const total = files.reduce((sum, file) => sum + (uploadProgress[file.name] ?? 0), 0);
        return Math.round(total / files.length);
    }, [files, uploadProgress]);

    const handleFileChange = (selected: File[]) => {
        setError(null);

        if (selected.length > MAX_FILES) {
            setError(`Selecione no máximo ${MAX_FILES} PDFs por lote.`);
            return;
        }

        const invalid = selected.find((file) => !isPdfFile(file));
        if (invalid) {
            setError(`O arquivo ${invalid.name} não parece ser um PDF.`);
            return;
        }

        const tooLarge = selected.find((file) => file.size > MAX_FILE_BYTES);
        if (tooLarge) {
            setError(`O arquivo ${tooLarge.name} ultrapassa o limite de 15 MB.`);
            return;
        }

        setFiles(selected);
        setUploadProgress({});
        setRejectedFiles([]);
    };

    const handleRemoveFile = (fileIndex: number) => {
        setFiles((current) => current.filter((_, index) => index !== fileIndex));
        setUploadProgress({});
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            setError('Selecione ao menos um PDF para importar.');
            return;
        }

        if (mode === 'report' && !sourceReportId) {
            setError('Salve o laudo antes de importar resultados para esta seção.');
            return;
        }

        setUploading(true);
        setError(null);
        setRejectedFiles([]);

        const selectedCount = files.length;

        try {
            const uploadedFiles = [];

            for (const file of files) {
                // The staging key goes to the batch endpoint, which validates
                // and promotes the object itself — never to /uploads/confirm.
                const key = await uploadsApi.uploadToStaging({
                    file,
                    intent: 'publisher_results',
                    onProgress: (progress) => {
                        setUploadProgress((current) => ({
                            ...current,
                            [file.name]: progress,
                        }));
                    },
                });

                uploadedFiles.push({ key, original_filename: file.name });
            }

            const { batch, rejectedFiles: rejected } = await publisherResultImportsApi.createBatch({
                patient_id: patientId,
                source_report_id: sourceReportId ?? null,
                files: uploadedFiles,
            });

            setCreatedBatch(batch);
            setBatchId(batch.id);
            setRejectedFiles(rejected);
            setFiles([]);
            if (fileInputRef.current) fileInputRef.current.value = '';
            onCompleted?.();

            // Some files may have been refused while others are already being
            // extracted; reporting unqualified success would hide that.
            toast.success(
                rejected.length
                    ? `${selectedCount - rejected.length} de ${selectedCount} PDFs enviados`
                    : 'PDF enviado para extração',
            );
        } catch (uploadError) {
            // A 400 here means no file survived, and it names each one.
            setRejectedFiles(getRejectedImportFiles(uploadError));
            setError(
                getErrorMessage(uploadError) || 'Não foi possível enviar os PDFs para importação.',
            );
        } finally {
            setUploading(false);
        }
    };

    const toggleTable = (importId: string, tableId: string) => {
        setSelection((current) => {
            const selected = new Set(current[importId] ?? []);
            if (selected.has(tableId)) {
                selected.delete(tableId);
            } else {
                selected.add(tableId);
            }

            return {
                ...current,
                [importId]: Array.from(selected),
            };
        });
    };

    const handleContinueDuplicate = async (record: PublisherResultImport) => {
        setContinuingId(record.id);
        setError(null);

        try {
            await publisherResultImportsApi.continueDuplicate(record.id);
            await batchQuery.refetch();
        } catch (continueError) {
            setError(
                getErrorMessage(continueError) ||
                    'Não foi possível continuar o processamento duplicado.',
            );
        } finally {
            setContinuingId(null);
        }
    };

    const handleCancel = async (record: PublisherResultImport) => {
        setCancellingId(record.id);
        setError(null);

        try {
            await publisherResultImportsApi.cancel(record.id);
            await batchQuery.refetch();
            onCompleted?.();
            // The row just disappears from the list, so say why.
            toast.success('Importação cancelada.');
        } catch (cancelError) {
            setError(getErrorMessage(cancelError) || 'Não foi possível cancelar a importação.');
        } finally {
            setCancellingId(null);
        }
    };

    const handleApprove = async (
        record: PublisherResultImport,
        review?: {
            selectedTableIds: string[];
            tableNarratives: Array<{ table_id: string; narrative: string | null }>;
        },
    ) => {
        const selectedTableIds = review?.selectedTableIds ?? selection[record.id] ?? [];
        if (selectedTableIds.length === 0) return;

        setApprovingId(record.id);
        setError(null);

        try {
            await onBeforeApprove?.();

            const result = await publisherResultImportsApi.approve(record.id, {
                selected_table_ids: selectedTableIds,
                ...(review?.tableNarratives.length
                    ? { table_narratives: review.tableNarratives }
                    : {}),
            });

            await onApproved?.(result);

            const appliedCount = result.applied_table_ids.length;
            const skippedCount = result.skipped_duplicate_table_ids.length;

            if (result.pending_report) {
                toast.success(
                    'Resultado aprovado. Ele será adicionado quando houver um laudo em andamento.',
                );
            } else if (mode === 'report') {
                toast.success(
                    appliedCount === 0 && skippedCount > 0
                        ? 'Estas tabelas já estavam no laudo.'
                        : skippedCount > 0
                          ? 'Tabelas novas importadas para o laudo. Duplicadas foram ignoradas.'
                          : 'Tabelas importadas para o laudo',
                );
            } else if (result.report) {
                toast.success(
                    appliedCount === 0 && skippedCount > 0
                        ? 'Resultado aprovado. As tabelas já estavam no laudo em andamento.'
                        : 'Resultado adicionado ao laudo em andamento',
                );
            } else {
                toast.success('Resultado importado para o histórico do paciente');
            }
            const refreshedBatch = await batchQuery.refetch();
            if (!initialImportId) {
                const nextReadyImport = refreshedBatch.data?.imports.find(
                    (nextRecord) =>
                        nextRecord.id !== record.id && nextRecord.status === 'ready_for_approval',
                );
                if (nextReadyImport) setSelectedImportId(nextReadyImport.id);
            }
            onCompleted?.();
            onReviewApproved?.(record.id);
        } catch (approveError) {
            setError(getErrorMessage(approveError) || 'Não foi possível aprovar esta importação.');
        } finally {
            setApprovingId(null);
        }
    };

    const handleClose = () => {
        if (hasBatch) onCompleted?.();
        onClose();
    };

    const renderImportCard = (record: PublisherResultImport) => (
        <ImportCard
            key={record.id}
            record={record}
            onContinueDuplicate={() => handleContinueDuplicate(record)}
            onCancel={() => handleCancel(record)}
            continuing={continuingId === record.id}
            cancelling={cancellingId === record.id}
        />
    );

    const hasUploadAction = !hasBatch && uploadTab === 'upload';
    const hasFooterActions = Boolean(reviewNavigation) || hasUploadAction;

    const uploadStep = (
        <div className="w-full space-y-4">
            <FileDropzone
                variant="card"
                title="Enviar PDFs de resultados"
                description={`Até ${MAX_FILES} arquivos PDF, 15 MB cada.`}
                selectLabel="Escolher arquivos"
                accept="application/pdf,.pdf"
                multiple
                loading={uploading}
                showSelectButton={false}
                inputRef={fileInputRef}
                onFilesSelected={handleFileChange}
            />

            {files.length > 0 && (
                <div className="space-y-2">
                    {files.map((file, index) => (
                        <div
                            key={`${file.name}-${file.size}-${index}`}
                            className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3 text-sm shadow-sm"
                        >
                            <div className="min-w-0">
                                <p className="truncate font-medium text-foreground">{file.name}</p>
                                <p className="text-xs text-muted-foreground">
                                    {(file.size / 1024 / 1024).toFixed(1)} MB
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveFile(index)}
                                disabled={uploading}
                                aria-label={`Remover ${file.name}`}
                                title="Remover arquivo"
                            >
                                <Trash className="size-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {uploading && <Progress value={uploadPercent} />}
        </div>
    );

    // An import waiting on approval owns the whole dialog: the guided review
    // renders its own layout, header trail and footer, and asks its questions
    // one at a time. Everything below is the upload and processing chrome,
    // which has nothing to say while a file is being reviewed.
    if (selectedImport?.status === 'ready_for_approval') {
        return (
            <PublisherResultGuidedReview
                key={selectedImport.id}
                record={selectedImport}
                reportData={reportData}
                selectedTableIds={activeSelectedTableIds}
                onToggleTable={(tableId) => toggleTable(selectedImport.id, tableId)}
                approving={isApprovingSelectedImport}
                cancelling={cancellingId === selectedImport.id}
                error={error}
                onApprove={(review) => handleApprove(selectedImport, review)}
                onDiscard={() => handleCancel(selectedImport)}
                onBackToMenu={onBackToMenu}
                onClose={handleClose}
                // In the pending queue the batch is filtered down to this one
                // import, so the position has to come from the queue itself —
                // `visibleImports` would always say "1 de 1" there.
                fileNavigation={
                    reviewNavigation
                        ? {
                              position: reviewNavigation.currentIndex + 1,
                              total: reviewNavigation.total,
                              onPrevious: reviewNavigation.onPrevious,
                              onNext: reviewNavigation.onNext,
                          }
                        : {
                              position: selectedImportPosition + 1,
                              total: visibleImports.length,
                          }
                }
            />
        );
    }

    return (
        <AppDialogLayout
            title={modalTitle}
            // The header already pads 20px below the title block; the body's own
            // 24px on top pushed the first control — the tabs — too far from it.
            bodyClassName="pt-2"
            backAction={
                onBackToMenu
                    ? {
                          label: 'Voltar às opções',
                          onClick: onBackToMenu,
                      }
                    : undefined
            }
            closeAction={{
                label: 'Fechar',
                onClick: handleClose,
            }}
            footer={
                hasFooterActions ? (
                    <WizardReviewStepFooter
                        total={reviewNavigation?.total ?? 1}
                        currentIndex={reviewNavigation?.currentIndex ?? 0}
                        onPrevious={reviewNavigation?.onPrevious}
                        onNext={reviewNavigation?.onNext}
                        onSelect={reviewNavigation?.onSelect}
                    >
                        {hasUploadAction && (
                            <Button
                                type="button"
                                onClick={handleUpload}
                                disabled={uploading || files.length === 0}
                            >
                                {uploading ? (
                                    <Spinner className="size-4 animate-spin" />
                                ) : (
                                    <ExportCurve className="size-4" />
                                )}
                                Importar resultado
                            </Button>
                        )}
                    </WizardReviewStepFooter>
                ) : undefined
            }
        >
            <div className="space-y-6">
                {!hasBatch &&
                    (showsHistory ? (
                        <SegmentedTabs
                            value={uploadTab}
                            onValueChange={(value) => setUploadTab(value as UploadTab)}
                            tabs={[
                                { value: 'upload', label: 'Enviar PDFs', content: uploadStep },
                                {
                                    value: 'history',
                                    label: 'Histórico',
                                    // A contagem renderiza até o zero; sem
                                    // importação a aba não leva badge nenhum.
                                    count: history.records.length || undefined,
                                    content: (
                                        <ReportImportHistoryList
                                            records={history.records}
                                            isLoading={history.isLoading}
                                            onOpenImport={(record) => {
                                                setSelectedImportId(record.id);
                                                setBatchId(record.batch_id);
                                            }}
                                            onRetry={() => setUploadTab('upload')}
                                        />
                                    ),
                                },
                            ]}
                        />
                    ) : (
                        uploadStep
                    ))}

                {error && (
                    <Alert variant="destructive" icon={<Danger className="size-4" />}>
                        {error}
                    </Alert>
                )}

                {rejectedFiles.length > 0 && (
                    <Alert variant="warning">
                        <p className="font-medium">
                            {rejectedFiles.length === 1
                                ? 'Um arquivo não pôde ser importado:'
                                : `${rejectedFiles.length} arquivos não puderam ser importados:`}
                        </p>
                        <ul className="mt-2 space-y-1 text-sm">
                            {rejectedFiles.map((rejected) => (
                                <li key={rejected.original_filename}>
                                    <span className="font-medium">
                                        {rejected.original_filename}
                                    </span>{' '}
                                    — {rejected.description}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {hasBatch && (
                    <div className="space-y-4">
                        {visibleImports.length === 0 ? (
                            <Alert variant="warning">
                                Esta importação não foi encontrada neste lote.
                            </Alert>
                        ) : isProcessingStep ? (
                            <>
                                {attentionImports.length > 0 && (
                                    <div className="space-y-3">
                                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                            Precisa da sua atenção
                                        </p>
                                        {attentionImports.map((record) => renderImportCard(record))}
                                    </div>
                                )}
                                <PublisherResultProcessingPanel
                                    imports={processingImports}
                                    cancellingId={cancellingId}
                                    onCancel={handleCancel}
                                    showIllustration={attentionImports.length === 0}
                                />
                            </>
                        ) : (
                            visibleImports.map((record) => renderImportCard(record))
                        )}
                    </div>
                )}
            </div>
        </AppDialogLayout>
    );
}
